import { Box, Link as MuiLink, Stack, Typography } from '@mui/material';
import type { GameMediaClip } from '../lib/api/games-api-client';

/**
 * `<GameMediaClips>` — "Related media" section on the Game page.
 *
 * Renders `media.clips` entries (round-27 reserved: podcast
 * segments, trailers, dev interviews). Returns `null` when the
 * list is empty so the section hides cleanly on games with no
 * related clips yet.
 */

interface GameMediaClipsProps {
  readonly clips: ReadonlyArray<GameMediaClip>;
}

const SECTION_TITLE = 'Related media';

export function GameMediaClips({ clips }: GameMediaClipsProps): JSX.Element | null {
  if (clips.length === 0) return null;
  return (
    <Box>
      <Typography variant="h2" component="h2" sx={{ fontSize: '1.25rem', fontWeight: 600, mb: 1.5 }}>
        {SECTION_TITLE}
      </Typography>
      <Stack spacing={1}>
        {clips.map((c, i) => <MediaClipRow key={`${c.source}-${i}`} clip={c} />)}
      </Stack>
    </Box>
  );
}

interface MediaClipRowProps { readonly clip: GameMediaClip; }

function MediaClipRow({ clip }: MediaClipRowProps): JSX.Element {
  const kindLabel = labelForKind(clip.kind);
  const durationLabel = formatDuration(clip.durationSeconds);
  return (
    <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <MuiLink href={clip.url} target="_blank" rel="noopener noreferrer" sx={{ fontWeight: 500 }}>
        {clip.title}
      </MuiLink>
      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
        {kindLabel} · {clip.source}{durationLabel.length > 0 ? ` · ${durationLabel}` : ''}
      </Typography>
    </Box>
  );
}

function labelForKind(kind: GameMediaClip['kind']): string {
  switch (kind) {
    case 'podcast-audio': return 'Podcast';
    case 'trailer-video': return 'Trailer';
    case 'interview-audio': return 'Interview (audio)';
    case 'interview-video': return 'Interview (video)';
  }
}

function formatDuration(seconds: number | undefined): string {
  if (seconds === undefined || seconds <= 0) return '';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}
